import type { ReactElement, ReactNode } from 'react'
import { Tabs as AriaTabs, type Key, Tab, TabList, TabPanel } from 'react-aria-components'
import { cn } from '../lib/cn'

/** One tab and the panel it reveals. */
export interface TabEntry {
	id: string
	label: string
	content: ReactNode
}

/**
 * Props for the {@link Tabs} atom.
 *
 * @property tabs - The tabs, in render order.
 * @property selectedKey - Controlled selection; the panel shown is this tab's.
 * @property onSelectionChange - Reports the activated tab's `id`.
 * @property className - Extra Tailwind classes merged onto the tab list.
 *
 * @example
 * ```tsx
 * const [tab, setTab] = useState<Key>('overview')
 *
 * <Tabs
 *   aria-label="Sections"
 *   tabs={[{ id: 'overview', label: 'Overview', content: <Overview /> }]}
 *   selectedKey={tab}
 *   onSelectionChange={setTab}
 * />
 * ```
 */
export interface TabsProps {
	tabs: TabEntry[]
	selectedKey: Key
	onSelectionChange: (key: Key) => void
	className?: string
	'aria-label'?: string
}

/**
 * Accessible tabs primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** one `role="tablist"` of `role="tab"` controls with
 *   Arrow-key navigation, and exactly one `role="tabpanel"` — the selected
 *   tab's. The selected tab carries `aria-selected="true"`.
 * - **Controlled:** the visible panel is derived from `selectedKey` alone.
 *   Activating a tab reports its `id` through `onSelectionChange`; the
 *   component does not swap the panel itself, so the consumer owns the
 *   selection (ADR-0031 decoupling seam).
 * - **Styling:** every colour, radius and spacing class is a `ds-*` token utility.
 *
 * @example
 * ```tsx
 * import { Tabs } from '@today/ui-primitives'
 *
 * const [tab, setTab] = useState<Key>('details')
 *
 * <Tabs
 *   aria-label="Record sections"
 *   selectedKey={tab}
 *   onSelectionChange={setTab}
 *   tabs={[
 *     { id: 'details', label: 'Details', content: <Details /> },
 *     { id: 'history', label: 'History', content: <History /> },
 *   ]}
 * />
 * ```
 *
 * @see packages/ui-primitives/src/tabs/tabs.stories.tsx for visual examples
 */
export function Tabs({ tabs, selectedKey, onSelectionChange, className, ...props }: TabsProps): ReactElement {
	return (
		<AriaTabs
			{...props}
			selectedKey={selectedKey}
			onSelectionChange={onSelectionChange}
			className="flex flex-col gap-3"
		>
			<TabList className={cn('flex gap-1 border-b border-ds-border', className)}>
				{tabs.map((tab) => (
					<Tab
						key={tab.id}
						id={tab.id}
						className="cursor-pointer rounded-t-ds-md px-3 py-2 text-ds-base text-ds-text-muted outline-none transition-colors data-[selected]:border-b-2 data-[selected]:border-ds-primary data-[selected]:text-ds-text data-[focus-visible]:ring-2 data-[focus-visible]:ring-ds-ring"
					>
						{tab.label}
					</Tab>
				))}
			</TabList>
			{tabs.map((tab) => (
				<TabPanel key={tab.id} id={tab.id} className="text-ds-base text-ds-text outline-none">
					{tab.content}
				</TabPanel>
			))}
		</AriaTabs>
	)
}
